import React from "react";
import {
  FaBed,
  FaCalendarAlt,
  FaConciergeBell,
  FaPencilAlt,
  FaTrash,
} from "react-icons/fa";

import {
  BORDER,
  GREEN,
  MUTED,
  formatDate,
  formatMoney,
  renderValue,
} from "./reservationViewUtils";

const SectionHeading = ({ icon: Icon, title, children }) => (
  <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
    <div className="d-flex align-items-center gap-2">
      <Icon style={{ color: GREEN, fontSize: 15 }} />
      <h5 className="mb-0" style={{ fontSize: 16, fontWeight: 600 }}>
        {title}
      </h5>
    </div>
    {children}
  </div>
);

const Divider = () => (
  <hr style={{ borderTop: `1px solid ${BORDER}`, opacity: 1, margin: "22px 0" }} />
);

const ReservationChargesPanel = ({
  roomDetails = [],
  noOfDays,
  addedServices = [],
  serviceTypes = [],
  itemOptions = [],
  newItem,
  onNewItemChange,
  onTypeChange,
  onItemChange,
  onSaveService,
  onEditService,
  onCancelServiceEdit,
  onDeleteService,
  onSaveServices,
  editingServiceIndex = null,
  canEditCharges = false,
  servicesDirty = false,
  saving = false,
  readOnlyReason,
}) => {
  const hasNightlyRates = roomDetails.some((room) => (room.rate || []).length);
  const isEditingService = editingServiceIndex !== null;

  return (
    <div className="card">
      <div className="card-body">
        <SectionHeading icon={FaBed} title="Rooms & Rates" />
        {roomDetails.length ? (
          <div className="table-responsive">
            <table className="table table-striped align-middle mb-0">
              <thead>
                <tr>
                  <th>Room</th>
                  <th>Plan</th>
                  <th>Qty</th>
                  <th>Nights</th>
                  <th>Guests</th>
                  <th>Assigned Rooms</th>
                  <th className="text-end">Rate</th>
                  <th className="text-end">Tax</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {roomDetails.map((room, index) => (
                  <tr key={`${room.id || room.name}-${index}`}>
                    <td>{renderValue(room.name)}</td>
                    <td>{renderValue(room.planName)}</td>
                    <td>{room.quantity || 1}</td>
                    <td>{renderValue(noOfDays)}</td>
                    <td>{renderValue(room.guests)}</td>
                    <td>
                      {(room.physicalRooms || []).length
                        ? (room.physicalRooms || [])
                            .map((unit) => unit?.label || unit?.id || unit)
                            .join(", ")
                        : "-"}
                    </td>
                    <td className="text-end">{formatMoney(room.roomTotalPrice)}</td>
                    <td className="text-end">{formatMoney(room.roomTotalTax)}</td>
                    <td className="text-end">
                      {formatMoney(
                        Number(room.roomTotalPrice || 0) + Number(room.roomTotalTax || 0)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mb-0" style={{ color: MUTED }}>
            No rooms are attached to this reservation.
          </p>
        )}

        {hasNightlyRates ? (
          <>
            <Divider />
            <SectionHeading icon={FaCalendarAlt} title="Nightly Breakdown" />
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Room</th>
                    <th>Date</th>
                    <th className="text-end">Rate / Night</th>
                    <th className="text-end">Tax</th>
                  </tr>
                </thead>
                <tbody>
                  {roomDetails.flatMap((room, roomIndex) =>
                    (room.rate || []).map((night, nightIndex) => (
                      <tr key={`${roomIndex}-${nightIndex}`}>
                        <td>{renderValue(room.name)}</td>
                        <td>{formatDate(night.date)}</td>
                        <td className="text-end">{formatMoney(night.ratePerNight)}</td>
                        <td className="text-end">{formatMoney(night.taxAmount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        <Divider />

        <SectionHeading icon={FaConciergeBell} title="Other Services">
          {canEditCharges ? (
            <div className="d-flex align-items-center gap-2">
              {servicesDirty ? (
                <span style={{ color: "#a2751a", fontSize: 13 }}>Unsaved changes</span>
              ) : null}
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                disabled={saving || !servicesDirty}
                onClick={onSaveServices}
              >
                {saving ? "Saving..." : "Save Charges"}
              </button>
            </div>
          ) : null}
        </SectionHeading>

        {addedServices.length ? (
          <div className="table-responsive">
            <table className="table table-striped align-middle mb-0">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Item</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th className="text-end">Rate</th>
                  <th className="text-end">Tax</th>
                  <th className="text-end">Total</th>
                  {canEditCharges ? <th className="text-end">Action</th> : null}
                </tr>
              </thead>
              <tbody>
                {addedServices.map((service, index) => (
                  <tr
                    key={`${service.itemId || service.item}-${index}`}
                    style={
                      editingServiceIndex === index ? { background: "#eaf7f3" } : undefined
                    }
                  >
                    <td>{renderValue(service.type)}</td>
                    <td>{renderValue(service.item)}</td>
                    <td>{renderValue(service.desc)}</td>
                    <td>{renderValue(service.quantity)}</td>
                    <td className="text-end">{formatMoney(service.rate)}</td>
                    <td className="text-end">{formatMoney(service.tax)}</td>
                    <td className="text-end">
                      {formatMoney(
                        Number(service.quantity || 0) * Number(service.rate || 0) +
                          Number(service.tax || 0)
                      )}
                    </td>
                    {canEditCharges ? (
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={saving}
                            title="Edit service"
                            onClick={() => onEditService(index)}
                          >
                            <FaPencilAlt />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={saving}
                            title="Remove service"
                            onClick={() => onDeleteService(index)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mb-0" style={{ color: MUTED }}>
            No extra services have been charged to this reservation.
          </p>
        )}

        {canEditCharges ? (
          <div
            className="mt-4"
            style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0" style={{ fontWeight: 600 }}>
                {isEditingService ? "Update Service" : "Add a Service"}
              </h6>
              {isEditingService ? (
                <span style={{ color: MUTED, fontSize: 13 }}>
                  Editing row {editingServiceIndex + 1}
                </span>
              ) : null}
            </div>
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={newItem.type}
                  disabled={saving}
                  onChange={(event) => onTypeChange(event.target.value)}
                >
                  <option value="">Select Type</option>
                  {serviceTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Item</label>
                <select
                  className="form-select"
                  value={newItem.item}
                  disabled={saving || !newItem.type}
                  onChange={(event) => onItemChange(event.target.value)}
                >
                  <option value="">Select Item</option>
                  {itemOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Quantity</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={newItem.quantity}
                  disabled={saving}
                  onChange={(event) =>
                    onNewItemChange({ ...newItem, quantity: Number(event.target.value) })
                  }
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Rate</label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  value={newItem.rate}
                  disabled={saving}
                  onChange={(event) =>
                    onNewItemChange({ ...newItem, rate: Number(event.target.value) })
                  }
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Tax</label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  value={newItem.tax}
                  disabled={saving}
                  onChange={(event) =>
                    onNewItemChange({ ...newItem, tax: Number(event.target.value) })
                  }
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Optional"
                  value={newItem.desc}
                  disabled={saving}
                  onChange={(event) =>
                    onNewItemChange({ ...newItem, desc: event.target.value })
                  }
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Total</label>
                <div className="form-control" style={{ background: "#f8f9fa" }}>
                  {formatMoney(
                    Number(newItem.quantity || 0) * Number(newItem.rate || 0) +
                      Number(newItem.tax || 0)
                  )}
                </div>
              </div>
              <div className="col-md-2 d-flex align-items-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary w-100"
                  disabled={saving}
                  onClick={onSaveService}
                >
                  {isEditingService ? "Update" : "Add"}
                </button>
                {isEditingService ? (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={saving}
                    onClick={onCancelServiceEdit}
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : readOnlyReason ? (
          <p className="mt-3 mb-0" style={{ color: MUTED, fontSize: 13 }}>
            {readOnlyReason}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default ReservationChargesPanel;
